import { Upload } from 'lucide-react';
import { useId, useRef, type ChangeEvent } from 'react';
import { cn } from '../../lib/cn';
import { useI18nTranslation } from '../../lib/use-i18n-translation';

/** The three types the service accepts; anything else is refused by its magic bytes (PP §8). */
export const DOCUMENT_ACCEPT = 'image/jpeg,image/png,application/pdf';
export const PHOTO_ACCEPT = 'image/jpeg,image/png';

export interface FileInputProps {
  id?: string;
  label: string;
  /** Muted line under the control — the limits, in the person's own language. */
  hint?: string;
  accept?: string;
  /**
   * `capture="environment"` on a phone opens the rear camera straight away, which is how a photo of a
   * car actually gets taken (PP §8: "camera/file selection supports mobile use"). Desktop browsers
   * ignore it and show the ordinary file picker.
   */
  capture?: boolean;
  disabled?: boolean;
  isBusy?: boolean;
  size?: 'sm' | 'default';
  className?: string;
  onSelect: (file: File) => void;
}

/**
 * A styled `<input type="file">` (plan §2.6).
 *
 * The native control is visually hidden rather than replaced: it keeps the keyboard behaviour, the
 * screen-reader announcement and the mobile camera integration that a `<div onClick>` would throw away,
 * and the `<label>` is what the person actually sees and clicks.
 *
 * The input is reset after every selection, so choosing the *same* file again after a failure still
 * fires `change` — otherwise "Retry" would silently do nothing, which is the one thing a retry must not do.
 */
export const FileInput = ({
  id,
  label,
  hint,
  accept = DOCUMENT_ACCEPT,
  capture = false,
  disabled = false,
  isBusy = false,
  size = 'default',
  className,
  onSelect,
}: FileInputProps) => {
  const { t } = useI18nTranslation();
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const inputRef = useRef<HTMLInputElement>(null);

  const onChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];

    if (file) {
      onSelect(file);
    }

    // Always clear: the same file must be selectable twice in a row after a failed upload.
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  return (
    <div className={cn('space-y-1.5', className)}>
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={accept}
        {...(capture ? { capture: 'environment' as const } : {})}
        disabled={disabled || isBusy}
        onChange={onChange}
        className="sr-only"
      />
      <label
        htmlFor={inputId}
        aria-disabled={disabled || isBusy}
        className={cn(
          'inline-flex cursor-pointer select-none items-center justify-center whitespace-nowrap rounded-md border border-border bg-background font-medium text-foreground transition-colors',
          'hover:bg-muted focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background',
          size === 'sm' ? 'h-8 gap-1.5 px-3 text-xs' : 'h-9 gap-2 px-4 text-sm',
          (disabled || isBusy) && 'pointer-events-none opacity-50',
        )}
      >
        <Upload className="size-3.5" />
        {isBusy ? t('documents.uploading') : label}
      </label>
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
};
